import React, { useState } from 'react';
import { Input, Button, Card, CardContent, Badge, Alert, Spinner } from '../components/ui';
import { Search, Zap, Workflow, Link2, ArrowRight } from 'lucide-react';
import { api } from '../services/api';

interface RelatedItem {
  type: 'tool' | 'prompt';
  id: string;
  name: string;
  relationship: 'primary' | 'secondary';
}

interface QueryResult {
  id: string;
  name: string;
  description: string;
  ardfType: string;
  score: number;
  serverName: string;
  uri: string;
  capabilities?: string[];
  related_items?: RelatedItem[];
}

export const Query: React.FC = () => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<QueryResult[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchPerformed, setSearchPerformed] = useState(false);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!query.trim()) {
      setError('Please enter a search query');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSearchPerformed(true);

      const response = await api.query.search(query);
      setResults(response.results || []);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Search failed');
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'tool':
        return 'info';
      case 'resource':
        return 'success';
      case 'prompt':
        return 'warning';
      default:
        return 'default';
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Hybrid Discovery Search</h1>
        <p className="text-gray-600 mt-1">
          Search across all your connected MCP servers with automatic tool↔prompt discovery
        </p>
        <div className="mt-2 flex items-center gap-2 text-sm text-blue-600">
          <Link2 size={16} />
          <span>Automatically discover related tools and prompts through bidirectional relationships</span>
        </div>
      </div>

      {/* Search Form */}
      <Card>
        <CardContent className="p-6">
          <form onSubmit={handleSearch} className="space-y-4">
            <div className="flex gap-2">
              <div className="flex-1">
                <Input
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  placeholder="Search for tools, resources, or prompts..."
                  fullWidth
                  disabled={loading}
                />
              </div>
              <Button
                type="submit"
                variant="primary"
                loading={loading}
                disabled={!query.trim()}
              >
                <Search size={18} className="mr-2" />
                Search
              </Button>
            </div>

            <div className="text-sm text-gray-600">
              <p className="font-medium mb-1">Search tips:</p>
              <ul className="list-disc list-inside space-y-1 text-xs">
                <li>Use natural language: "read files from disk" or "send slack messages"</li>
                <li>Combines full-text and semantic search for best results</li>
                <li>Results are ranked by relevance</li>
              </ul>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Spinner size="lg" />
        </div>
      )}

      {/* Results */}
      {!loading && searchPerformed && (
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              {results.length} {results.length === 1 ? 'Result' : 'Results'}
            </h2>
          </div>

          {results.length === 0 ? (
            <Card>
              <CardContent className="p-12 text-center">
                <p className="text-gray-600">
                  No results found. Try a different search query or connect more servers.
                </p>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-3">
              {results.map((result) => (
                <Card key={result.id} hover>
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {result.name}
                          </h3>
                          <Badge variant={getTypeColor(result.ardfType)} size="sm">
                            {result.ardfType}
                          </Badge>
                          {result.score && (
                            <Badge variant="default" size="sm">
                              {Math.round(result.score * 100)}% match
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-3">
                          {result.description}
                        </p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <Zap size={14} />
                            {result.serverName}
                          </span>
                          {result.capabilities && result.capabilities.length > 0 && (
                            <span>
                              Capabilities: {result.capabilities.join(', ')}
                            </span>
                          )}
                        </div>

                        {/* Hybrid Discovery: Related Items */}
                        {result.related_items && result.related_items.length > 0 && (
                          <div className="mt-4 pt-4 border-t border-gray-100">
                            <div className="flex items-center gap-2 mb-2">
                              <Link2 size={14} className="text-blue-600" />
                              <span className="text-xs font-medium text-gray-700">
                                Related Items ({result.related_items.length})
                              </span>
                            </div>
                            <div className="flex flex-wrap gap-2">
                              {result.related_items.map((relatedItem, idx) => (
                                <div
                                  key={idx}
                                  className="flex items-center gap-1.5 px-2 py-1 bg-blue-50 border border-blue-200 rounded-lg text-xs"
                                >
                                  {relatedItem.type === 'prompt' ? (
                                    <Workflow size={12} className="text-blue-600" />
                                  ) : (
                                    <Zap size={12} className="text-blue-600" />
                                  )}
                                  <span className="text-blue-900 font-medium">
                                    {relatedItem.name}
                                  </span>
                                  <Badge
                                    variant={relatedItem.relationship === 'primary' ? 'info' : 'default'}
                                    size="sm"
                                  >
                                    {relatedItem.type}
                                  </Badge>
                                  {relatedItem.relationship === 'primary' && (
                                    <ArrowRight size={10} className="text-blue-600" />
                                  )}
                                </div>
                              ))}
                            </div>
                            <p className="text-xs text-gray-500 mt-2">
                              💡 Hybrid Discovery: These items are automatically related through workflow definitions
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};
